import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User as UserIcon, Phone, AlertCircle, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const AuthModal = () => {
  const { authModalState, closeAuthModal, login, register, forgotPassword } = useAuth();
  const { open, mode: initialMode, onSuccess, intent } = authModalState;
  const [mode, setMode] = useState(initialMode || 'login');
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode || 'login');
      setForm({ email: '', password: '', name: '', phone: '' });
      setError(''); setSuccess(''); setLoading(false);
    }
  }, [open, initialMode]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      let user;
      if (mode === 'login') {
        user = await login(form.email.trim(), form.password);
      } else if (mode === 'signup') {
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); setLoading(false); return; }
        user = await register({ email: form.email.trim(), password: form.password, name: form.name.trim(), phone: form.phone.trim() });
      } else if (mode === 'forgot') {
        await forgotPassword(form.email.trim());
        setSuccess('If an account exists with this email, a reset link has been sent.');
        setLoading(false);
        return;
      }
      setLoading(false);
      if (onSuccess && user) onSuccess(user);
      closeAuthModal();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const titleByMode = { login: 'Welcome back', signup: 'Create your account', forgot: 'Forgot password' };
  const ctaByMode = { login: 'Sign in', signup: 'Create account', forgot: 'Send reset link' };
  const subTitle = intent === 'wishlist' ? 'Save & sync your shortlisted properties across devices' :
                    intent === 'saved-search' ? 'Save searches and get email alerts when new properties match' :
                    'Sign in to save properties, run alerts, and unlock advisor features';

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeAuthModal} data-testid="auth-modal">
      <div className="bg-obsidian-card border border-brand/30 rounded-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-gradient-to-br from-brand/15 to-purple-500/10 px-6 pt-6 pb-5">
          <button onClick={closeAuthModal} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="auth-modal-close"><X size={18} /></button>
          <h2 className="font-outfit text-xl font-semibold text-white">{titleByMode[mode]}</h2>
          <p className="text-white/50 text-xs mt-1">{subTitle}</p>
        </div>

        <div className="p-6">
          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-md p-3 mb-4 text-red-400 text-sm" data-testid="auth-error">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 rounded-md p-3 mb-4 text-green-400 text-sm">
              <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" /><span>{success}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-white/40 text-[11px] font-medium mb-1.5 uppercase tracking-wider">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  data-testid="auth-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-obsidian border border-white/10 rounded-md h-11 pl-9 pr-3 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-brand/60"
                  placeholder="you@example.com" autoFocus
                />
              </div>
            </div>

            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-white/40 text-[11px] font-medium mb-1.5 uppercase tracking-wider">Full name</label>
                  <div className="relative">
                    <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      data-testid="auth-name" type="text" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full bg-obsidian border border-white/10 rounded-md h-11 pl-9 pr-3 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-brand/60"
                      placeholder="Your name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/40 text-[11px] font-medium mb-1.5 uppercase tracking-wider">Phone (optional)</label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      data-testid="auth-phone" type="tel" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full bg-obsidian border border-white/10 rounded-md h-11 pl-9 pr-3 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-brand/60"
                      placeholder="+91 9XXXX XXXXX"
                    />
                  </div>
                </div>
              </>
            )}

            {mode !== 'forgot' && (
              <div>
                <label className="block text-white/40 text-[11px] font-medium mb-1.5 uppercase tracking-wider">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    data-testid="auth-password" type={showPw ? 'text' : 'password'} required minLength={mode === 'signup' ? 8 : 1}
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full bg-obsidian border border-white/10 rounded-md h-11 pl-9 pr-9 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-brand/60"
                    placeholder={mode === 'signup' ? 'Min. 8 characters' : 'Your password'}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {mode === 'login' && (
                  <button type="button" onClick={() => setMode('forgot')} data-testid="auth-forgot-link" className="text-brand text-[11px] mt-2 hover:underline">
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-brand-gradient text-white font-semibold h-11 rounded-md text-sm uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <><Loader2 size={14} className="animate-spin" /> Please wait...</> : ctaByMode[mode]}
            </button>

            <div className="text-center text-white/40 text-xs pt-2">
              {mode === 'login' && (
                <>New to Prop Assist? <button type="button" onClick={() => setMode('signup')} data-testid="switch-to-signup" className="text-brand hover:underline">Create an account</button></>
              )}
              {mode === 'signup' && (
                <>Already a member? <button type="button" onClick={() => setMode('login')} data-testid="switch-to-login" className="text-brand hover:underline">Sign in</button></>
              )}
              {mode === 'forgot' && (
                <>Remember your password? <button type="button" onClick={() => setMode('login')} className="text-brand hover:underline">Sign in</button></>
              )}
            </div>
          </form>
        </div>

        <p className="text-center text-white/15 text-[10px] pb-4 px-4">
          By continuing, you agree to Prop Assist's <a href="/terms-of-service" className="hover:text-white/40">Terms</a> and <a href="/privacy-policy" className="hover:text-white/40">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
